import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiAlertTriangle,
  FiRefreshCw,
  FiFilter,
  FiX,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCheckCircle,
  FiTrendingUp,
  FiFlag,
  FiPlay,
  FiSave,
  FiArrowUp,
} from 'react-icons/fi';
import { lraService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatCard from '../../components/StatCard';
import { formatDate, formatDateTime } from '../../utils/formatters';

const AuditSignals = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [signalType, setSignalType] = useState('');
  const [page, setPage] = useState(1);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const limit = 25;

  // ============ Queries ============
  const {
    data,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery(
    ['lra-audit-signals', { severity, status, signalType, page, limit }],
    () =>
      lraService.listAuditSignals({
        severity,
        status,
        signal_type: signalType,
        page,
        limit,
      }),
    { keepPreviousData: true }
  );

  const signals = data?.data || [];
  const summary = data?.summary || {};
  const pagination = data?.pagination || {};
  const totalPages = pagination.total_pages || 1;
  const total = pagination.total || 0;

  // ============ Mutations ============
  const updateMutation = useMutation(
    ({ id, payload }) => lraService.updateAuditSignal(id, payload),
    {
      onSuccess: () => {
        toast.success('Signal updated');
        queryClient.invalidateQueries('lra-audit-signals');
        setSelectedSignal(null);
      },
      onError: (err) => {
        toast.error(
          err.response?.data?.message || 'Failed to update signal'
        );
      },
    }
  );

  // ============ Handlers ============
  const hasFilters = severity || status || signalType;

  const clearFilters = () => {
    setSeverity('');
    setStatus('');
    setSignalType('');
    setPage(1);
  };

  const handleRunDetection = async () => {
    setDetecting(true);
    try {
      const response = await lraService.runDetection();
      if (response.success) {
        const created = response.data?.signals_created || 0;
        const updated = response.data?.signals_updated || 0;
        const scanned = response.data?.businesses_scanned || 0;

        toast.success(
          `Scanned ${scanned} ${scanned === 1 ? 'business' : 'businesses'} — ` +
            `${created} new, ${updated} updated`,
          { duration: 5000 }
        );
        queryClient.invalidateQueries('lra-audit-signals');
      } else {
        toast.error(response.message || 'Detection failed');
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || 'Detection failed'
      );
    } finally {
      setDetecting(false);
    }
  };

  const handleStatusChange = (signal, newStatus) => {
    updateMutation.mutate({
      id: signal.signal_id,
      payload: { status: newStatus },
    });
  };

  const handlePrev = () => {
    if (page > 1) setPage(page - 1);
  };
  const handleNext = () => {
    if (page < totalPages) setPage(page + 1);
  };

  // ============ Render ============
  if (isLoading) return <LoadingSpinner message="Loading audit signals..." />;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Signals</h1>
          <p className="page-subtitle">
            Flagged businesses requiring review — {total} total signal
            {total === 1 ? '' : 's'}
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={handleRunDetection}
            disabled={detecting}
            style={{
              background: detecting ? '#9ca3af' : '#dc2626',
            }}
          >
            {detecting ? (
              <>
                <FiRefreshCw
                  size={18}
                  style={{ animation: 'spin 1s linear infinite' }}
                />
                Detecting...
              </>
            ) : (
              <>
                <FiPlay size={18} />
                Run Detection
              </>
            )}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="stats-grid">
        <StatCard
          icon={<FiAlertTriangle size={24} />}
          value={summary.open_count || 0}
          label="Open"
          color="orange"
        />
        <StatCard
          icon={<FiClock size={24} />}
          value={summary.reviewing_count || 0}
          label="Under Review"
          color="blue"
        />
        <StatCard
          icon={<FiCheckCircle size={24} />}
          value={summary.cleared_count || 0}
          label="Cleared"
          color="green"
        />
        <StatCard
          icon={<FiFlag size={24} />}
          value={summary.high_count || 0}
          label="High Severity"
          color="red"
        />
      </div>

      {/* Info banner — how detection works */}
      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #93c5fd',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          fontSize: '13px',
          color: '#1e3a8a',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <FiAlertTriangle size={18} />
        <div>
          <strong>How detection works:</strong> Click "Run Detection" to scan
          all businesses for suspicious patterns (sales variance, stock
          variance, sudden drops, low stock activity, payment mismatch).
          Duplicate issues update existing signals rather than creating new
          ones.
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
          }}
        >
          <FiFilter size={16} color="#6b7280" />
          <span
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            Filters
          </span>
          {hasFilters && (
            <button
              className="btn btn-secondary"
              onClick={clearFilters}
              style={{
                padding: '4px 10px',
                fontSize: '12px',
                marginLeft: 'auto',
              }}
            >
              <FiX size={14} />
              Clear
            </button>
          )}
        </div>

        <div className="form-row">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Severity</label>
            <select
              className="form-select"
              value={severity}
              onChange={(e) => {
                setSeverity(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Severities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="reviewing">Reviewing</option>
              <option value="cleared">Cleared</option>
              <option value="escalated">Escalated</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Signal Type</label>
            <select
              className="form-select"
              value={signalType}
              onChange={(e) => {
                setSignalType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Types</option>
              <option value="sales_variance">Sales Variance</option>
              <option value="stock_variance">Stock Variance</option>
              <option value="sudden_drop">Sudden Drop</option>
              <option value="low_stock_activity">Low Stock Activity</option>
              <option value="payment_mismatch">Payment Mismatch</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table or empty state */}
      {signals.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <FiCheckCircle size={48} color="#22c55e" />
            <h3>
              {hasFilters
                ? 'No signals match your filters'
                : 'No audit signals'}
            </h3>
            <p>
              {hasFilters
                ? 'Try adjusting your filters'
                : 'Click "Run Detection" above to scan businesses for suspicious patterns.'}
            </p>
            {hasFilters && (
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Signal</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Detected</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {signals.map((s) => (
                  <tr key={s.signal_id}>
                    <td>
                      <div>
                        <strong>{s.business_name || 'Unknown'}</strong>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#9ca3af',
                            fontFamily: 'monospace',
                            marginTop: 2,
                          }}
                        >
                          {s.dssn}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: '13px',
                            textTransform: 'capitalize',
                          }}
                        >
                          {(s.signal_type || '').replace(/_/g, ' ')}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            marginTop: 2,
                          }}
                        >
                          {s.title}
                        </div>
                      </div>
                    </td>
                    <td>
                      <SeverityBadge severity={s.severity} />
                    </td>
                    <td>
                      {/* Quick status change */}
                      <select
                        value={s.status}
                        onChange={(e) => handleStatusChange(s, e.target.value)}
                        disabled={updateMutation.isLoading}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '12px',
                          fontWeight: 600,
                          background:
                            s.status === 'open'
                              ? '#fef3c7'
                              : s.status === 'reviewing'
                              ? '#dbeafe'
                              : s.status === 'cleared'
                              ? '#dcfce7'
                              : '#fee2e2',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="open">Open</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="cleared">Cleared</option>
                        <option value="escalated">Escalated</option>
                      </select>
                    </td>
                    <td>{formatDate(s.detected_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="icon-btn"
                          onClick={() => setSelectedSignal(s)}
                          title="View details"
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() =>
                            navigate(`/lra/businesses/${s.dssn}`)
                          }
                          title="View business"
                        >
                          <FiTrendingUp size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
                padding: '12px 16px',
                background: '#f9fafb',
                borderRadius: '8px',
              }}
            >
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                Page {page} of {totalPages} • {total} total
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handlePrev}
                  disabled={page === 1}
                >
                  <FiChevronLeft size={16} />
                  Previous
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleNext}
                  disabled={page >= totalPages}
                >
                  Next
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {selectedSignal && (
        <SignalDetailModal
          signal={selectedSignal}
          onClose={() => setSelectedSignal(null)}
          onViewBusiness={() => {
            const dssn = selectedSignal.dssn;
            setSelectedSignal(null);
            navigate(`/lra/businesses/${dssn}`);
          }}
          onSave={(payload) =>
            updateMutation.mutate({
              id: selectedSignal.signal_id,
              payload,
            })
          }
          saving={updateMutation.isLoading}
        />
      )}
    </div>
  );
};

// =============================================
// Detail Modal
// =============================================
const SignalDetailModal = ({
  signal,
  onClose,
  onViewBusiness,
  onSave,
  saving,
}) => {
  const [status, setStatus] = useState(signal.status);
  const [notes, setNotes] = useState(signal.notes || '');

  const hasChanges =
    status !== signal.status || notes !== (signal.notes || '');

  const handleSave = () => {
    const payload = {};
    if (status !== signal.status) payload.status = status;
    if (notes !== (signal.notes || '')) payload.notes = notes;
    onSave(payload);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '640px',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 700,
                margin: 0,
                marginBottom: 6,
              }}
            >
              {signal.title || 'Audit Signal'}
            </h2>
            <div
              style={{
                fontSize: '13px',
                color: '#6b7280',
                fontFamily: 'monospace',
              }}
            >
              {signal.signal_id}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Badges */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            flexWrap: 'wrap',
          }}
        >
          <SeverityBadge severity={signal.severity} />
          <span
            style={{
              background: '#f3f4f6',
              color: '#374151',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'capitalize',
            }}
          >
            {(signal.signal_type || '').replace(/_/g, ' ')}
          </span>
        </div>

        {/* Business */}
        <DetailSection label="Business">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>
                {signal.business_name || 'Unknown'}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  fontFamily: 'monospace',
                  marginTop: 2,
                }}
              >
                {signal.dssn}
              </div>
            </div>
            <button
              className="btn btn-secondary"
              onClick={onViewBusiness}
              style={{ padding: '6px 14px', fontSize: '13px' }}
            >
              View Profile
            </button>
          </div>
        </DetailSection>

        {/* Description */}
        {signal.description && (
          <DetailSection label="Description">
            <p
              style={{
                margin: 0,
                fontSize: '14px',
                lineHeight: 1.6,
                color: '#374151',
                whiteSpace: 'pre-wrap',
              }}
            >
              {signal.description}
            </p>
          </DetailSection>
        )}

        {/* Evidence */}
        {signal.evidence && (
          <DetailSection label="Evidence">
            <pre
              style={{
                margin: 0,
                background: '#f9fafb',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '200px',
                color: '#374151',
              }}
            >
              {typeof signal.evidence === 'string'
                ? signal.evidence
                : JSON.stringify(signal.evidence, null, 2)}
            </pre>
          </DetailSection>
        )}

        {/* Status controls */}
        <DetailSection label="Status">
          <div
            style={{
              display: 'flex',
              gap: '6px',
              flexWrap: 'wrap',
            }}
          >
            {['open', 'reviewing', 'cleared', 'escalated'].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`btn ${
                  status === s ? 'btn-primary' : 'btn-secondary'
                }`}
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  textTransform: 'capitalize',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </DetailSection>

        {/* Notes */}
        <DetailSection label="Notes">
          <textarea
            className="form-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add your review notes here..."
            rows={4}
          />
        </DetailSection>

        {/* Timeline */}
        <DetailSection label="Timeline">
          <InfoLine
            label="Detected"
            value={formatDateTime(signal.detected_at)}
          />
          {signal.reviewed_at && (
            <InfoLine
              label="Reviewed"
              value={formatDateTime(signal.reviewed_at)}
            />
          )}
          {signal.reviewed_by && (
            <InfoLine label="Reviewed By" value={signal.reviewed_by} />
          )}
          {signal.cleared_at && (
            <InfoLine
              label="Cleared"
              value={formatDateTime(signal.cleared_at)}
            />
          )}
        </DetailSection>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            <FiSave size={16} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================
// Small helpers
// =============================================
const DetailSection = ({ label, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '8px',
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const InfoLine = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '6px 0',
      fontSize: '13px',
    }}
  >
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span style={{ fontWeight: 500 }}>{value}</span>
  </div>
);

const SeverityBadge = ({ severity }) => {
  const colors = {
    high: { bg: '#fee2e2', color: '#991b1b' },
    medium: { bg: '#fef3c7', color: '#92400e' },
    low: { bg: '#f3f4f6', color: '#6b7280' },
  };
  const c = colors[severity] || colors.low;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {severity}
    </span>
  );
};

export default AuditSignals;
